<template>
  <v-dialog :value="true" width="900" persistent scrollable>
    <v-card>
      <AdCardTitle dialog>{{ $t('add-artefact') }}</AdCardTitle>
      <v-card-text class="pa-5">
        <editable-dropdown
          v-model="type"
          :items="types"
          item-value="key"
          @change="onTypeChange"
        />

        <!-- Dataset -->
        <v-combobox
          v-if="type === 'ds'"
          ref="dataset"
          class="required"
          v-model="input"
          :label="$t('dataset')"
          :items="datasetItems"
          :loading="false"
          :disabled="false"
          item-value="id"
          :filter="datasetSearchFilter"
          clearable
        >
          <template #item="{ item }">
            <AdStatusDot larger tooltip class="mr-3" :status="item.statusCode" :icon="$filters.getDatasetIcon(item.type)"/>
            {{ $filters.getMatchingTranslation({ value: item.name }) }}
          </template>
          <template #selection="{ item }">
            {{ item.id }}
            <v-chip v-if="item.displayName || item.name" class="ml-2" small outlined :disabled="false">
              <AdStatusDot class="mr-2" tooltip :status="item.statusCode" :icon="$filters.getDatasetIcon(item.type)"/>
              {{ $filters.getMatchingTranslation({ value: item.name }) }}
            </v-chip>
          </template>
        </v-combobox>

        <!-- Template -->
        <v-combobox
          v-if="type === 'tm'"
          ref="template"
          class="required"
          v-model="input"
          :label="$t('template')"
          :items="templateItems"
          :loading="false"
          :disabled="false"
          item-value="id"
          :filter="templateSearchFilter"
          clearable
        >
          <template #item="{ item }">
            <AdStatusDot larger tooltip class="mr-3" :status="item.statusCode" :icon="$filters.getTemplateIcon(item.type)"/>
            {{ $filters.getMatchingTranslation({ value: item.displayName }) }}
          </template>
          <template #selection="{ item }">
            {{ item.id }}
            <v-chip v-if="item.displayName" class="ml-2" small outlined :disabled="false">
              <AdStatusDot class="mr-2" tooltip :status="item.statusCode" :icon="$filters.getTemplateIcon(item.type)"/>
              {{ $filters.getMatchingTranslation({ value: item.displayName }) }}
            </v-chip>
          </template>
        </v-combobox>

        <!-- Valueset -->
        <v-combobox
          v-if="type === 'vs'"
          ref="valueset"
          class="required"
          v-model="input"
          :label="$t('value-set')"
          :items="valuesetItems"
          :loading="false"
          :disabled="false"
          item-value="id"
          :filter="valuesetSearchFilter"
          clearable
        >
          <template #item="{ item }">
            <AdStatusDot larger class="mr-3" tooltip :status="item.statusCode" :icon="$filters.getValuesetIcon(item.type)"/>
            {{ $filters.getMatchingTranslation({ value: item.name }) }}
          </template>
          <template #selection="{ item }">
            {{ item.id }}
            <v-chip v-if="item.displayName || item.name" class="ml-2" small outlined :disabled="false">
              <AdStatusDot class="mr-2" tooltip :status="item.statusCode" :icon="$filters.getValuesetIcon(item.type)"/>
              {{ $filters.getMatchingTranslation({ value: item.name }) }}
            </v-chip>
          </template>
        </v-combobox>
      </v-card-text>
      <v-card-actions class="dialogbottomstrip">
        <v-spacer></v-spacer>
        <AdButton type="cancel" @click="closeDialog"/>
        <AdButton
          type="add"
          :hint="$t('add-artefact')"
          @click="addArtefact"
          depressed
          :disabled="!formIsValid"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'

export default Vue.extend({
  props: {
    object: {
      type: Object,
      required: false,
      default: null
    }
  },

  data () {
    return {
      type: 'ds',
      input: '',
      types: [{
        text: this.$t('dataset'),
        key: 'ds'
      }, {
        text: this.$t('template'),
        key: 'tm'
      }, {
        text: this.$t('value-set'),
        key: 'vs'
      }]
    }
  },

  computed: {
    formIsValid () {
      return this.type && this.input
    },

    datasetItems () {
      return this.$store.state.dataset.datasetItems || []
    },

    templateItems () {
      return this.$store.state.rule.template.templates?.map((template) => template.template).flat() || []
    },

    valuesetItems () {
      return this.$store.state.terminology.valueSet || []
    },

    editorValue () {
      if (this.type === 'ds') {
        return {
          type: 'ds',
          href: `ds::${this.input?.id}/${this.input?.effectiveDate}`,
          label: this.$filters.getMatchingTranslation({ value: this.input?.name })
        }
      }

      if (this.type === 'tm') {
        return {
          type: 'tm',
          href: `tm::${this.input?.id}/${this.input?.effectiveDate}`,
          label: this.$filters.getMatchingTranslation({ value: this.input?.name })
        }
      }

      if (this.type === 'vs') {
        return {
          type: 'vs',
          href: `vs::${this.input?.id}/${this.input?.effectiveDate}`,
          label: this.$filters.getMatchingTranslation({ value: this.input?.name })
        }
      }

      if (this.type === 'cs') {
        return {
          type: 'cs',
          href: `cs::${this.input?.id}/${this.input?.effectiveDate}`,
          label: this.$filters.getMatchingTranslation({ value: this.input?.name })
        }
      }

      if (this.type === 'cm') {
        return {
          type: 'cm',
          href: `cm::${this.input?.id}/${this.input?.effectiveDate}`,
          label: this.$filters.getMatchingTranslation({ value: this.input?.name })
        }
      }

      return null
    },

    watchedVariables () {
      return [
        this.object,
        this.datasetItems.length,
        this.templateItems.length,
        this.valuesetItems.length
      ]
    }
  },

  watch: {
    watchedVariables: {
      immediate: true,
      deep: true,
      handler () {
        this.type = this.object?.type || 'ds'
        this.input = {
          ds: this.datasetItems,
          tm: this.templateItems,
          vs: this.valuesetItems
        }?.[this.type]?.find((item) => {
          return item.id === this.object?.id &&
            item.effectiveDate === this.object?.version
        }) || null
      }
    }
  },

  mounted () {
    if (!this.datasetItems.length) this.$store.dispatch('dataset/loadDatasetItems')
    if (!this.valuesetItems.length) this.$store.dispatch('terminology/getValueSetList')
    if (!this.templateItems.length) this.$store.dispatch('rule/template/setTemplateList')
  },

  methods: {
    onTypeChange () {
      // Reset input.
      this.input = ''
    },

    datasetSearchFilter (item, queryText) {
      return [item?.id, this.$filters.getMatchingTranslation({ value: item?.name })]
        .map(text => text?.toLowerCase())
        .some(text => text?.includes(queryText.toLowerCase()))
    },

    templateSearchFilter (item, queryText) {
      return [item?.id, this.$filters.getMatchingTranslation({ value: item?.displayName })]
        .map(text => text?.toLowerCase())
        .some(text => text?.includes(queryText.toLowerCase()))
    },

    valuesetSearchFilter (item, queryText) {
      return [item?.id, this.$filters.getMatchingTranslation({ value: item?.name })]
        .map(text => text?.toLowerCase())
        .some(text => text?.includes(queryText.toLowerCase()))
    },

    closeDialog () {
      this.$emit('close')
    },

    addArtefact () {
      if (this.editorValue) {
        this.$emit('add', this.editorValue)
      }
    }
  }
})
</script>
